import { DefaultScoreValueMapping, ScoreValueMapping, SelectionSummary, SelectionSummaryWrapper, SurveyQuestion, SurveyScore, SurveyScoreSummary, SurveyScoreValues } from '../../DTOs';
import { surveyQuestionHelper } from '../../helpers/surveyQuestionHelper';
import { surveyQuestionFieldValidation } from '../fieldValidation/fields/surveyQuestionFieldValidation';
import { SurveyScoreStats } from './SurveyScoreStats';

export interface ISelectionStats 
{
    data:SelectionSummary;
    totalSelections:number;
}

export class UnscoredSelectionStats implements ISelectionStats
{
    public data:SelectionSummary;

    public totalSelections:number;

    constructor(
        selectionReport:SelectionSummary
    ){
        this.data = selectionReport;
        this.totalSelections = this.data.optionValueCounts.reduce((acc, ovc) => acc + ovc.count, 0);
    }

    static aggregate(list:SelectionSummary[]):UnscoredSelectionStats
    {
        const combinedSummaries =  SelectionSummaryWrapper.aggregate(list).data;
        return new UnscoredSelectionStats(combinedSummaries);
    }
}

export class ScoredSelectionStats implements ISelectionStats
{
    public data:SelectionSummary;
    public totalSelections:number;
    
    public scoreMapping: ScoreValueMapping[];

    public score:SurveyScoreStats;

    constructor(
        summary:SelectionSummary,
        scoreMapping: ScoreValueMapping[]
    ){
        this.data = summary;
        this.scoreMapping = scoreMapping;

        if(this.data.optionValueCounts.length !== scoreMapping.length)
            throw new Error(`Mismatch in optionValueCounts(${JSON.stringify(this.data.optionValueCounts)}) and scoreMapping(${JSON.stringify(scoreMapping)}) lengths`);

        const scoreMapSet = new Set<string>(scoreMapping.map(sm => sm.value));
        if(scoreMapSet.size !== scoreMapping.length)
            throw new Error(`Duplicate values in scoreMapping: ${JSON.stringify(scoreMapping)}`);

        for(const ovc of this.data.optionValueCounts){
            const matchingScoreMapping = scoreMapping.find(sm => sm.value === ovc.value);
            if(!matchingScoreMapping)
                throw new Error(`Missing score mapping for option value ${ovc.value}`);
        }
        
        const scoreMap = new Map<string, SurveyScore>();
        for(const sm of scoreMapping){
            scoreMap.set(sm.value, sm.score);
        }
        
        const scoreCounts: SurveyScoreSummary = {
            score1Count:this.data.optionValueCounts.reduce((acc, ovc) => acc + (scoreMap.get(ovc.value) === 1 ? ovc.count : 0), 0),
            score2Count:this.data.optionValueCounts.reduce((acc, ovc) => acc + (scoreMap.get(ovc.value) === 2 ? ovc.count : 0), 0),
            score3Count:this.data.optionValueCounts.reduce((acc, ovc) => acc + (scoreMap.get(ovc.value) === 3 ? ovc.count : 0), 0),
            score4Count:this.data.optionValueCounts.reduce((acc, ovc) => acc + (scoreMap.get(ovc.value) === 4 ? ovc.count : 0), 0),
            score5Count:this.data.optionValueCounts.reduce((acc, ovc) => acc + (scoreMap.get(ovc.value) === 5 ? ovc.count : 0), 0)
        };
        this.score = new SurveyScoreStats(scoreCounts);

        this.totalSelections = this.score.totalSelections;
    }

    static aggregate(list:SelectionSummary[], scoreMapping: ScoreValueMapping[]):ScoredSelectionStats
    {
        const combinedSummaries = SelectionSummaryWrapper.aggregate(list).data;
        return new ScoredSelectionStats(combinedSummaries, scoreMapping);
    }

    static aggregateStats(list:ScoredSelectionStats[]):ScoredSelectionStats
    {
        if(list.length === 0)
            throw new Error('Cannot aggregate empty list of ScoredSelectionStats');

        const data = SelectionSummaryWrapper.aggregate(list.map(s => s.data)).data;
        return new ScoredSelectionStats(data, DefaultScoreValueMapping);
    }

    static newDefaultSelectionStats():ScoredSelectionStats
    {
        return new ScoredSelectionStats(
            SelectionSummaryWrapper.createDefault().data,
            DefaultScoreValueMapping
        );
    }

    static createNewSelectionStats(question:SurveyQuestion):ISelectionStats
    {
        if(surveyQuestionHelper.isScoredQuestion(question)){
            return new ScoredSelectionStats(
                SelectionSummaryWrapper.createFromQuestion(question).data,
                surveyQuestionHelper.getScoreMapping(question)
            );
        }
        else
            return new UnscoredSelectionStats(
                SelectionSummaryWrapper.createFromQuestion(question).data,
            );
    }

    static fromDefaultCounts(value1Count:number, value2Count:number, value3Count:number, value4Count:number, value5Count:number):ScoredSelectionStats
    {
        return new ScoredSelectionStats(
            SelectionSummaryWrapper.fromDefaultCounts(value1Count, value2Count, value3Count, value4Count, value5Count).data,
            DefaultScoreValueMapping
        );
    }

    static fromScore1AndScore5Counts(score1Count:number, score5Count:number):ScoredSelectionStats
    {
        return ScoredSelectionStats.fromDefaultCounts(score1Count, 0, 0, 0, score5Count);
    }

    static fromCustomCounts(optionValueCounts:{value:string, count:number, score:SurveyScore}[]):ScoredSelectionStats
    {
        const scoreMapping = optionValueCounts.map(ovc => ({ value: ovc.value, score: ovc.score }));
        const selectionSummary:SelectionSummary = {
            optionValueCounts: optionValueCounts.map(ovc => ({ value: ovc.value, count: ovc.count }))
        };
        return new ScoredSelectionStats(selectionSummary, scoreMapping);
    }

    static toDefaultQuestionValues(stats:ScoredSelectionStats):ScoredSelectionStats
    {
        // Extra checks to ensure that data is well formed
        // 1. All optionValueCounts values must be present in the scoreMapping
        if(stats.data.optionValueCounts.some(ovc => !stats.scoreMapping.some(sm => sm.value === ovc.value)))
            throw new Error('Mismatch in optionValueCounts and scoreMapping values');
        // 2. All scoreMapping values must be valid scores for the question
        if(stats.scoreMapping.some(sm => !surveyQuestionFieldValidation.surveyQuestionOption.score(sm.score)))
            throw new Error('Invalid score value in scoreMapping');


        // Blank summary where custom values are mapped to default values
        // allowing status to be aggregated across questions with different (scored) custom values
        const defaultMappedSummary: SelectionSummary = {
            optionValueCounts: []
        };
        // Iterate possible score values
        for(const scoreValue of SurveyScoreValues){

            // Find all custom values that map to the current score value
            const scoreValueMappings = stats.scoreMapping.filter(sm => sm.score === scoreValue);
            const scoreValues = stats.data.optionValueCounts
                .filter(ovc => scoreValueMappings.some(sm => sm.value === ovc.value));
            const scoreCount = scoreValues.reduce((acc, ovc) => acc + ovc.count, 0);

            defaultMappedSummary.optionValueCounts.push({
                value: scoreValue.toString(),
                count: scoreCount
            });
        }

        return new ScoredSelectionStats(defaultMappedSummary, DefaultScoreValueMapping);
    }
}